/**
 * DEFCON TERMINAL - Connection Sequence Animations
 *
 * Unique connection animations and login sequences for each of the
 * four game systems. Each has distinct visual styling reflecting
 * its origin (US Military, Soviet, CIA, KGB).
 *
 * @module ConnectionSequence
 */

import type { PathType } from '@shared/types';
import { SYSTEM_NAMES, TIMING } from '@shared/constants';
import type { DiscoveredSystem } from './PhoneGenerator';

/**
 * Connection sequence frame
 */
export interface ConnectionFrame {
  lines: string[];
  delay: number; // ms to display this frame
  sound?: string; // optional sound effect
  clearScreen?: boolean; // clear before displaying
}

/**
 * Connection sequence complete callback
 */
export type ConnectionCompleteCallback = (systemType: PathType) => void;

/**
 * Connection sequence animation manager
 */
export class ConnectionSequence {
  private isRunning: boolean = false;
  private currentSequence: ConnectionFrame[] = [];
  private frameIndex: number = 0;

  constructor() {}

  /**
   * Get connection sequence for a system
   */
  getSequence(system: DiscoveredSystem): ConnectionFrame[] {
    switch (system.systemType) {
      case 'sarn-west':
        return this.getSarnWestSequence(system);
      case 'sarn-east':
        return this.getSarnEastSequence(system);
      case 'phantom-directive':
        return this.getPhantomDirectiveSequence(system);
      case 'red-shadow':
        return this.getRedShadowSequence(system);
      default:
        return this.getGenericSequence(system);
    }
  }

  /**
   * Run connection sequence
   */
  async runSequence(
    system: DiscoveredSystem,
    onFrame: (frame: ConnectionFrame, index: number, total: number) => void,
    onComplete?: ConnectionCompleteCallback
  ): Promise<void> {
    this.currentSequence = this.getSequence(system);
    this.frameIndex = 0;
    this.isRunning = true;

    for (let i = 0; i < this.currentSequence.length && this.isRunning; i++) {
      const frame = this.currentSequence[i];
      this.frameIndex = i;
      onFrame(frame, i, this.currentSequence.length);
      await this.delay(frame.delay);
    }

    if (this.isRunning && onComplete) {
      onComplete(system.systemType);
    }

    this.isRunning = false;
  }

  /**
   * Stop running sequence
   */
  stop(): void {
    this.isRunning = false;
  }

  /**
   * Check if sequence is running
   */
  isActive(): boolean {
    return this.isRunning;
  }

  /**
   * SARN-WEST (US Military) connection sequence
   */
  private getSarnWestSequence(system: DiscoveredSystem): ConnectionFrame[] {
    const sarnConfig = SYSTEM_NAMES['sarn-west'];

    return [
      // Modem handshake
      {
        lines: [
          '',
          'ATDT ' + system.phoneNumber,
          '',
        ],
        delay: 500,
      },
      {
        lines: [
          '',
          'ATDT ' + system.phoneNumber,
          'RINGING...',
          '',
        ],
        delay: 1000,
      },
      {
        lines: [
          '',
          'CONNECT 9600',
          '',
        ],
        delay: 800,
        sound: 'modemConnect',
      },
      // US Military banner
      {
        lines: [
          '',
          '',
          '\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588',
          '\u2588                                                              \u2588',
          '\u2588        UNITED STATES DEPARTMENT OF DEFENSE                   \u2588',
          '\u2588                                                              \u2588',
          '\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588',
          '',
        ],
        delay: 1500,
        clearScreen: true,
      },
      {
        lines: [
          '',
          '================================================================',
          '           ' + sarnConfig.name,
          '    ' + sarnConfig.fullName,
          '           ' + sarnConfig.subtitle,
          '================================================================',
          '',
          '        *** CLASSIFIED - TOP SECRET - SCI ***',
          '',
          '  WARNING: This is a Department of Defense computer system.',
          '  Unauthorized access attempts will be logged and prosecuted.',
          '',
          '  All activity on this system is monitored and recorded.',
          '',
          '================================================================',
          '',
        ],
        delay: 2000,
      },
      // Login prompt
      {
        lines: [
          '',
          sarnConfig.accessPrompt,
          '',
          '  ENTER USER IDENTIFICATION:',
          '  > _',
          '',
        ],
        delay: 1000,
      },
      // Simulated login
      {
        lines: [
          '',
          '  ENTER USER IDENTIFICATION:',
          '  > OPERATOR',
          '',
          '  ENTER ACCESS CODE:',
          '  > ********',
          '',
          '  AUTHENTICATING...',
          '',
        ],
        delay: 1500,
      },
      // Access granted
      {
        lines: [
          '',
          '\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588',
          '\u2588                                              \u2588',
          '\u2588          ACCESS GRANTED                      \u2588',
          '\u2588          WELCOME TO S.A.R.N.                 \u2588',
          '\u2588                                              \u2588',
          '\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588',
          '',
          '  CURRENT DEFCON STATUS: 3 - ROUND HOUSE',
          '  TIME: 02:47:33 GMT',
          '  DATE: 1984-06-15',
          '',
          '  LOADING COMMAND INTERFACE...',
          '',
        ],
        delay: 2000,
        sound: 'success',
      },
    ];
  }

  /**
   * SARN-EAST (Soviet) connection sequence with Cyrillic headers
   */
  private getSarnEastSequence(system: DiscoveredSystem): ConnectionFrame[] {
    const sarnConfig = SYSTEM_NAMES['sarn-east'];

    return [
      // Modem handshake
      {
        lines: [
          '',
          'ATDT ' + system.phoneNumber,
          '',
        ],
        delay: 500,
      },
      {
        lines: [
          '',
          'CONNECT 4800',
          '',
          '\u041F\u041E\u0414\u041A\u041B\u042E\u0427\u0415\u041D\u0418\u0415...',
          '(CONNECTING...)',
          '',
        ],
        delay: 1200,
        sound: 'modemConnect',
      },
      // Soviet banner with translation mode
      {
        lines: [
          '',
          '\u2554\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2557',
          '\u2551                                                            \u2551',
          '\u2551      \u041C\u0418\u041D\u0418\u0421\u0422\u0415\u0420\u0421\u0422\u0412\u041E \u041E\u0411\u041E\u0420\u041E\u041D\u042B \u0421\u0421\u0421\u0420                      \u2551',
          '\u2551      MINISTRY OF DEFENSE USSR                             \u2551',
          '\u2551                                                            \u2551',
          '\u255A\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u255D',
          '',
        ],
        delay: 1500,
        clearScreen: true,
      },
      {
        lines: [
          '',
          '  \u2554\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2557',
          '  \u2551                                              \u2551',
          '  \u2551            ' + sarnConfig.name + '                      \u2551',
          '  \u2551            ' + sarnConfig.englishName + '                        \u2551',
          '  \u2551                                              \u2551',
          '  \u2551    ' + sarnConfig.russianFullName + '    \u2551',
          '  \u2551    ' + sarnConfig.fullName + '         \u2551',
          '  \u2551                                              \u2551',
          '  \u255A\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u255D',
          '',
          '  [TRANSLATION MODE: ENABLED]',
          '',
        ],
        delay: 2000,
      },
      {
        lines: [
          '',
          '  \u0412\u041D\u0418\u041C\u0410\u041D\u0418\u0415: \u0421\u0415\u041A\u0420\u0415\u0422\u041D\u0410\u042F \u0421\u0418\u0421\u0422\u0415\u041C\u0410',
          '  WARNING: CLASSIFIED SYSTEM',
          '',
          '  \u041D\u0415\u0421\u0410\u041D\u041A\u0426\u0418\u041E\u041D\u0418\u0420\u041E\u0412\u0410\u041D\u041D\u042B\u0419 \u0414\u041E\u0421\u0422\u0423\u041F \u0417\u0410\u041F\u0420\u0415\u0429\u0415\u041D',
          '  UNAUTHORIZED ACCESS IS FORBIDDEN',
          '',
          '  \u0412\u0421\u0415 \u0414\u0415\u0419\u0421\u0422\u0412\u0418\u042F \u0420\u0415\u0413\u0418\u0421\u0422\u0420\u0418\u0420\u0423\u042E\u0422\u0421\u042F',
          '  ALL ACTIONS ARE LOGGED',
          '',
        ],
        delay: 1800,
      },
      // Login sequence
      {
        lines: [
          '',
          '  \u0412\u0412\u0415\u0414\u0418\u0422\u0415 \u041A\u041E\u0414 \u0414\u041E\u0421\u0422\u0423\u041F\u0410:',
          '  ' + sarnConfig.accessPrompt,
          '',
          '  > ********',
          '',
          '  \u041F\u0420\u041E\u0412\u0415\u0420\u041A\u0410...',
          '  VERIFYING...',
          '',
        ],
        delay: 1500,
      },
      // Access granted
      {
        lines: [
          '',
          '  \u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588',
          '  \u2588                                            \u2588',
          '  \u2588          \u0414\u041E\u0421\u0422\u0423\u041F \u0420\u0410\u0417\u0420\u0415\u0428\u0415\u041D                     \u2588',
          '  \u2588          ACCESS GRANTED                    \u2588',
          '  \u2588                                            \u2588',
          '  \u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588',
          '',
          '  \u0421\u0422\u0410\u0422\u0423\u0421: \u0423\u0420\u041E\u0412\u0415\u041D\u042C \u0413\u041E\u0422\u041E\u0412\u041D\u041E\u0421\u0422\u0418 2',
          '  STATUS: READINESS LEVEL 2',
          '',
          '  \u0417\u0410\u0413\u0420\u0423\u0417\u041A\u0410 \u0418\u041D\u0422\u0415\u0420\u0424\u0415\u0419\u0421\u0410...',
          '  LOADING INTERFACE...',
          '',
        ],
        delay: 2000,
        sound: 'success',
      },
    ];
  }

  /**
   * PHANTOM DIRECTIVE (CIA) connection sequence
   */
  private getPhantomDirectiveSequence(system: DiscoveredSystem): ConnectionFrame[] {
    const phantomConfig = SYSTEM_NAMES['phantom-directive'];

    return [
      // Modem connection
      {
        lines: [
          '',
          'ATDT ' + system.phoneNumber,
          '',
        ],
        delay: 500,
      },
      {
        lines: [
          '',
          'CONNECT 14400',
          '',
          'INITIATING SECURE HANDSHAKE...',
          '',
        ],
        delay: 800,
        sound: 'modemConnect',
      },
      // Encrypted handshake animation
      {
        lines: [
          '',
          '  SECURE HANDSHAKE PROTOCOL',
          '  \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500',
          '',
          '  STEP 1/4: KEY EXCHANGE...',
          '  [########------------] 40%',
          '',
        ],
        delay: 600,
        clearScreen: true,
      },
      {
        lines: [
          '',
          '  SECURE HANDSHAKE PROTOCOL',
          '  \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500',
          '',
          '  STEP 2/4: CIPHER NEGOTIATION...',
          '  [##############------] 70%',
          '',
        ],
        delay: 600,
      },
      {
        lines: [
          '',
          '  SECURE HANDSHAKE PROTOCOL',
          '  \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500',
          '',
          '  STEP 3/4: CHANNEL ENCRYPTION...',
          '  [##################--] 90%',
          '',
        ],
        delay: 600,
      },
      {
        lines: [
          '',
          '  SECURE HANDSHAKE PROTOCOL',
          '  \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500',
          '',
          '  STEP 4/4: SESSION ESTABLISHED',
          '  [####################] 100%',
          '',
          '  >> SECURE CHANNEL ACTIVE <<',
          '',
        ],
        delay: 800,
      },
      // Biometric bypass
      {
        lines: [
          '',
          '\u250C\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2510',
          '\u2502  BIOMETRIC VERIFICATION SYSTEM                  \u2502',
          '\u251C\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2524',
          '\u2502                                                  \u2502',
          '\u2502    VOICEPRINT ANALYSIS: BYPASSED                 \u2502',
          '\u2502    RETINAL SCAN: BYPASSED                        \u2502',
          '\u2502    FINGERPRINT: BYPASSED                         \u2502',
          '\u2502                                                  \u2502',
          '\u2502    >> MAINTENANCE MODE ACTIVE <<                 \u2502',
          '\u2502                                                  \u2502',
          '\u2514\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2518',
          '',
        ],
        delay: 1500,
      },
      // CIA banner
      {
        lines: [
          '',
          '    \u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588',
          '    \u2588                                                \u2588',
          '    \u2588        CENTRAL INTELLIGENCE AGENCY             \u2588',
          '    \u2588                                                \u2588',
          '    \u2588        ' + phantomConfig.name + '                  \u2588',
          '    \u2588        ' + phantomConfig.fullName + '        \u2588',
          '    \u2588                                                \u2588',
          '    \u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588',
          '',
        ],
        delay: 1800,
        clearScreen: true,
      },
      {
        lines: [
          '',
          '  CLASSIFICATION: TOP SECRET // SAP // ORCON',
          '',
          '  \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500',
          '  SPECIAL ACCESS PROGRAM - COMPARTMENTED',
          '  DISSEMINATION RESTRICTED TO BIGOT LIST ONLY',
          '  \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500',
          '',
          '  ' + phantomConfig.accessPrompt,
          '',
          '  LOADING OPERATIONS DASHBOARD...',
          '',
        ],
        delay: 2000,
        sound: 'success',
      },
    ];
  }

  /**
   * RED SHADOW (KGB) connection sequence with burst transmission
   */
  private getRedShadowSequence(system: DiscoveredSystem): ConnectionFrame[] {
    const redShadowConfig = SYSTEM_NAMES['red-shadow'];

    return [
      // Initial connection
      {
        lines: [
          '',
          'ATDT ' + system.phoneNumber,
          '',
        ],
        delay: 500,
      },
      {
        lines: [
          '',
          'CONNECT 2400',
          '',
          'BURST TRANSMISSION DETECTED...',
          '',
        ],
        delay: 800,
        sound: 'modemConnect',
      },
      // Burst transmission decode animation
      {
        lines: [
          '',
          '  BURST TRANSMISSION DECODE',
          '  \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500',
          '',
          '  RECEIVING: ||||||||||______________________',
          '             25%',
          '',
          '  SIGNAL: \u2588\u2591\u2588\u2591\u2591\u2588\u2588\u2591\u2588\u2591\u2588\u2588\u2591\u2591\u2588\u2591\u2588\u2588\u2588\u2591\u2591\u2588\u2591\u2588\u2591\u2588\u2588\u2591\u2588\u2591',
          '',
        ],
        delay: 500,
        clearScreen: true,
      },
      {
        lines: [
          '',
          '  BURST TRANSMISSION DECODE',
          '  \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500',
          '',
          '  RECEIVING: ||||||||||||||||||______________',
          '             60%',
          '',
          '  SIGNAL: \u2591\u2588\u2588\u2591\u2588\u2591\u2591\u2588\u2591\u2588\u2591\u2588\u2588\u2588\u2591\u2588\u2588\u2591\u2591\u2588\u2591\u2588\u2588\u2591\u2588\u2591\u2591\u2588\u2588\u2591',
          '',
        ],
        delay: 500,
      },
      {
        lines: [
          '',
          '  BURST TRANSMISSION DECODE',
          '  \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500',
          '',
          '  RECEIVING: ||||||||||||||||||||||||||||||||',
          '             100% COMPLETE',
          '',
          '  DECODING TRANSMISSION...',
          '',
        ],
        delay: 800,
      },
      // KGB banner with Russian
      {
        lines: [
          '',
          '  \u2554\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2557',
          '  \u2551                                                        \u2551',
          '  \u2551        \u041A\u041E\u041C\u0418\u0422\u0415\u0422 \u0413\u041E\u0421\u0423\u0414\u0410\u0420\u0421\u0422\u0412\u0415\u041D\u041D\u041E\u0419 \u0411\u0415\u0417\u041E\u041F\u0410\u0421\u041D\u041E\u0421\u0422\u0418           \u2551',
          '  \u2551        COMMITTEE FOR STATE SECURITY                    \u2551',
          '  \u2551                                                        \u2551',
          '  \u2551                  ' + redShadowConfig.name + '                        \u2551',
          '  \u2551                  ' + redShadowConfig.englishName + '                           \u2551',
          '  \u2551                                                        \u2551',
          '  \u2551        ' + redShadowConfig.fullName + '          \u2551',
          '  \u2551                                                        \u2551',
          '  \u255A\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u255D',
          '',
        ],
        delay: 2000,
        clearScreen: true,
      },
      // Access granted
      {
        lines: [
          '',
          '  \u0421\u0415\u041A\u0420\u0415\u0422\u041D\u0410\u042F \u0421\u0418\u0421\u0422\u0415\u041C\u0410 \u0421\u0412\u042F\u0417\u0418 - \u0422\u041E\u041B\u042C\u041A\u041E \u0414\u041B\u042F \u041D\u0415\u041B\u0415\u0413\u0410\u041B\u041E\u0412',
          '  SECURE COMMUNICATIONS - ILLEGALS ONLY',
          '',
          '  \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500',
          '',
          '  ' + redShadowConfig.accessPrompt,
          '  WELCOME, COMRADE COORDINATOR',
          '',
          '  \u041E\u041F\u0415\u0420\u0410\u0422\u0418\u0412\u041D\u042B\u0419 \u0421\u0422\u0410\u0422\u0423\u0421: \u0410\u041A\u0422\u0418\u0412\u041D\u042B\u0419',
          '  OPERATIONAL STATUS: ACTIVE',
          '',
          '  \u0417\u0410\u0413\u0420\u0423\u0417\u041A\u0410 \u0418\u041D\u0422\u0415\u0420\u0424\u0415\u0419\u0421\u0410 \u041E\u041F\u0415\u0420\u0410\u0426\u0418\u0419...',
          '  LOADING OPERATIONS INTERFACE...',
          '',
        ],
        delay: 2000,
        sound: 'success',
      },
    ];
  }

  /**
   * Generic connection sequence (fallback)
   */
  private getGenericSequence(system: DiscoveredSystem): ConnectionFrame[] {
    return [
      {
        lines: [
          '',
          'ATDT ' + system.phoneNumber,
          '',
        ],
        delay: 500,
      },
      {
        lines: [
          '',
          'CONNECT ' + system.connectionBaudRate,
          '',
          'ESTABLISHING CONNECTION...',
          '',
        ],
        delay: 1000,
        sound: 'modemConnect',
      },
      {
        lines: [
          '',
          'CONNECTION ESTABLISHED',
          '',
          'SYSTEM: ' + system.systemName,
          '',
          'LOADING...',
          '',
        ],
        delay: 1500,
        sound: 'success',
      },
    ];
  }

  /**
   * Generate ASCII art for connection
   */
  static generateConnectionArt(systemType: PathType): string[] {
    switch (systemType) {
      case 'sarn-west':
        return [
          '           _____',
          '          /     \\',
          '         | () () |',
          '          \\  ^  /',
          '           |||||',
          '           |||||',
          '      STRATEGIC COMMAND',
        ];

      case 'sarn-east':
        return [
          '         \u2605\u2605\u2605\u2605\u2605',
          '        \u2605     \u2605',
          '       \u2605  \u262D  \u2605',
          '        \u2605     \u2605',
          '         \u2605\u2605\u2605\u2605\u2605',
          '      \u041F\u0415\u0420\u0418\u041C\u0415\u0422\u0420-2',
        ];

      case 'phantom-directive':
        return [
          '        .---.--.',
          '       /   .   \\',
          '      |   (_)   |',
          '       \\   .   /',
          '        `--\'--\'',
          '     PHANTOM DIRECTIVE',
        ];

      case 'red-shadow':
        return [
          '       _/\\_/\\_',
          '      /_\\ /_\\',
          '      \\_/ \\_/',
          '       \\_\\_/',
          '      \u041A\u0420\u0410\u0421\u041D\u0410\u042F \u0422\u0415\u041D\u042C',
        ];

      default:
        return ['SYSTEM'];
    }
  }

  /**
   * Helper: Promise-based delay
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

export default ConnectionSequence;
